import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { ScopeTypeCreate, ScopeType } from '@/lib/schemas';

/**
 * Query key for scope types list
 */
export const scopeTypesKeys = {
  all: ['scope-types'] as const,
  lists: () => [...scopeTypesKeys.all, 'list'] as const,
  list: () => [...scopeTypesKeys.lists()] as const,
  details: () => [...scopeTypesKeys.all, 'detail'] as const,
  detail: (id: string) => [...scopeTypesKeys.details(), id] as const,
};

/**
 * Hook to fetch all scope types
 */
export function useScopeTypes() {
  return useQuery({
    queryKey: scopeTypesKeys.list(),
    queryFn: async () => {
      const response = await api.scopeTypes.list();
      return response.scope_types;
    },
    staleTime: 5 * 60_000, // 5 min — reference data, rarely changes
  });
}

/**
 * Hook to fetch a single scope type by ID
 */
export function useScopeType(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: scopeTypesKeys.detail(id),
    queryFn: async () => {
      const response = await api.scopeTypes.get(id);
      return response.scope_type;
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to create a new scope type
 */
export function useCreateScopeType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ScopeTypeCreate) => {
      const response = await api.scopeTypes.create(data);
      return response.scope_type;
    },
    onSuccess: (newScopeType: ScopeType) => {
      // Optimistically add the new scope type to the cache immediately
      // This ensures dropdowns show the new type right away without waiting for refetch
      queryClient.setQueryData<ScopeType[]>(
        scopeTypesKeys.list(),
        (oldData) => {
          if (!oldData) return [newScopeType];
          return [...oldData, newScopeType];
        }
      );

      // Don't invalidate — we just set the cache from the server response.
      // Invalidating would trigger a refetch that can overwrite this data with stale reads.
      toast.success(`Scope type "${newScopeType.display_name}" created successfully`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create scope type. Please try again.');
    },
  });
}

/**
 * Hook to update a scope type
 */
export function useUpdateScopeType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ScopeTypeCreate> }) => {
      const response = await api.scopeTypes.update(id, data);
      return response.scope_type;
    },
    onSuccess: (updatedScopeType: ScopeType) => {
      // Invalidate both list and detail queries
      queryClient.invalidateQueries({ queryKey: scopeTypesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: scopeTypesKeys.detail(updatedScopeType.id) });

      toast.success(`Scope type "${updatedScopeType.display_name}" updated successfully`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update scope type. Please try again.');
    },
  });
}

/**
 * Hook to delete a scope type
 * Uses optimistic updates for immediate UI feedback (Issue #1)
 */
export function useDeleteScopeType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.scopeTypes.delete(id);
      return id;
    },
    onMutate: async (id) => {
      // Cancel outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: scopeTypesKeys.lists() });

      // Snapshot previous value for rollback
      const previousScopeTypes = queryClient.getQueriesData({ queryKey: scopeTypesKeys.lists() });

      // Optimistically remove the deleted scope type from cache
      queryClient.setQueriesData<ScopeType[]>(
        { queryKey: scopeTypesKeys.lists() },
        (old) => {
          if (!old) return old;
          return old.filter((scopeType) => scopeType.id !== id);
        }
      );

      return { previousScopeTypes };
    },
    onError: (error: any, id, context) => {
      // Rollback on error - restore previous data
      if (context?.previousScopeTypes) {
        context.previousScopeTypes.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error(error.message || 'Failed to delete scope type. Please try again.');
    },
    onSuccess: () => {
      // Cache already updated optimistically - just show success message
      toast.success('Scope type deleted successfully');
    },
  });
}
